import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {ViewService} from "../../services/view/view.service";
import {AuditLog, AuditLogPage, PerformerTypeEnum} from "../../generated/sales-terminal-client";
import {Delta, getDiff, PathsDiff} from "json-difference";
import {EditedPath} from "json-difference/dist/models/jsondiffer.model";
import {JsonDifferenceModalComponent} from "./json-difference-modal/json-difference-modal.component";
import {UtilService} from "../../services/util/util.service";

export interface SimplifiedAuditLog {
  id: string;
  timestamp: string;
  performer: string;
  entityType: string;
  rawBefore: string;
  rawAfter: string;
  action: string;
  entries: AuditEntry[];
}

export interface AuditEntry {
  changedField: string;
  before: string;
  after: string;
}

@Component({
  selector: 'audit-log-table',
  templateUrl: './audit-log-table.component.html',
  styleUrls: ['./audit-log-table.component.scss']
})
export class AuditLogTableComponent implements OnChanges {
  @Input() page: number;
  @Output() pageChange = new EventEmitter<number>();
  @Input() pageData: AuditLogPage;
  @Input() showEntityType: boolean;
  @Input() showAction: boolean;
  public simplifiedAuditLogs: SimplifiedAuditLog[] = [];
  public auditLogRowExpanded: Record<string, boolean> = {};
  private readonly SYSTEM: PerformerTypeEnum = 'SYSTEM';

  constructor(
    public viewService: ViewService,
    private dialog: MatDialog,
    public utilService: UtilService
  ) { }

  public emitPageChange(page: number): void {
    this.page = page;
    this.pageChange.emit(page);
  }

  public showJsonDifferenceModal(log: SimplifiedAuditLog) {
    this.dialog.open(JsonDifferenceModalComponent, {
      maxHeight: '100%',
      minWidth: '50%',
      data: { before: log.rawBefore, after: log.rawAfter }
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.pageData) {
      this.simplifiedAuditLogs = this.parseJsonDiff(changes.pageData.currentValue);
    }
  }

  public toggleAuditLogRow(rowId: string): void {
    this.auditLogRowExpanded[rowId] = !this.auditLogRowExpanded[rowId];
  }

  private parseJsonDiff(auditPage: AuditLogPage): SimplifiedAuditLog[] {
    return auditPage?.items.map((auditLog: AuditLog): SimplifiedAuditLog => {
      const logBefore = auditLog.before ? JSON.parse(auditLog.before) : {};
      const logAfter = auditLog.after ? JSON.parse(auditLog.after) : {};
      const auditDiff: Delta = getDiff(logBefore, logAfter, true);

      if (auditDiff.edited.length || auditDiff.added.length || auditDiff.removed.length) {
        return this.buildSimplifiedAuditLog(auditDiff, auditLog);
      }
      return null;
    }).filter(log => log != null)
      .sort((first, second) => second.timestamp.localeCompare(first.timestamp));
  }

  private buildSimplifiedAuditLog(auditDiff: Delta, auditLog: AuditLog): SimplifiedAuditLog {
    const simplifiedAuditLog: SimplifiedAuditLog = this.createSimplifiedAuditLogWithoutEntries(auditLog);

    auditDiff.added.forEach((diff: PathsDiff) => {
      const field = diff[0];
      const afterValue = diff[1];
      if (this.isValidValue(afterValue)) {
        const entry = this.createAddedEntry(afterValue, field);
        simplifiedAuditLog.entries.push(entry);
      }
    });
    auditDiff.removed.forEach((diff: PathsDiff) => {
      const field = diff[0];
      const beforeValue = diff[1];
      if (this.isValidValue(beforeValue)) {
        const entry = this.createRemovedEntry(beforeValue, field);
        simplifiedAuditLog.entries.push(entry);
      }
    });
    auditDiff.edited.forEach((diff: EditedPath) => {
      const entry = this.createEditedEntry(diff);
      simplifiedAuditLog.entries.push(entry);
    });
    return simplifiedAuditLog;
  }

  private isValidValue(value: any) {
    return value && !(Array.isArray(value) && value.length === 0) && JSON.stringify(value) !== "{}";
  }

  public isEmpty(value: string): boolean {
    return value == null || value === '';
  }

  private createSimplifiedAuditLogWithoutEntries(auditLog: AuditLog): SimplifiedAuditLog {
    return {
      id: crypto.randomUUID(),
      timestamp: auditLog.timestamp,
      performer: auditLog.performerType !== this.SYSTEM ? auditLog.performerEmail : this.SYSTEM,
      rawBefore: auditLog.before,
      rawAfter: auditLog.after,
      entityType: auditLog.entityType,
      action: auditLog.action,
      entries: []
    };
  }

  private createAddedEntry(afterValue: string, field: string): AuditEntry {
    return {
      before: '',
      after: afterValue,
      changedField: this.simplifyChangedFieldName(field)
    };
  }

  private createRemovedEntry(beforeValue: string, field: string): AuditEntry {
    return {
      before: beforeValue,
      after: '',
      changedField: this.simplifyChangedFieldName(field)
    };
  }

  private createEditedEntry(diff: EditedPath): AuditEntry {
    const field = diff[0];
    const beforeValue = diff[1];
    const afterValue = diff[2];
    return {
      before: beforeValue,
      after: afterValue,
      changedField: this.simplifyChangedFieldName(field)
    };
  }

  private simplifyChangedFieldName(field: string) {
    const arrayPattern = /\[(\d+)]/g;
    const separatorPattern = /\./g;
    return field
      .replace(arrayPattern, (_, index) => ` (${Number(index) + 1})`)
      .replace(separatorPattern, ' -> ');
  }

}
